import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchApi } from '../../../helpers/fetch';
import { AnalyticsSubtabListSkeleton } from '../../components/Skeletons/BatchScreenSkeletons';
import Icon from '../../components/Icon';
import { GENRE_ICONS } from '../../constants/genres';
import { formatEurosAmount } from '../../../helpers/money';
import './CreationsAnalytics.scss';

function CreationsAnalytics({ userId }) {
  const navigate = useNavigate();
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchCreations = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await fetchApi(`/api/creations/${userId}?page=1&pageSize=100`);
      const list = response?.success && Array.isArray(response?.data) ? response.data : [];
      setCreations(
        list.map((c) => ({
          ...c,
          views: c.total_views ?? c.views ?? c.view_count ?? 0,
        }))
      );
    } catch {
      setCreations([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchCreations();
    }
  }, [userId, fetchCreations]);

  const filteredCreations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return creations;
    return creations.filter(
      (c) =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.category || '').toLowerCase().includes(q)
    );
  }, [creations, search]);

  const handleCreationClick = (creation) => {
    navigate(`/creator-analytics/creations/${creation.creation_id}`);
  };

  const handleCreationKeyDown = (e, creation) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCreationClick(creation);
    }
  };

  const handleFilterClick = () => {
    // Filter modal/panel placeholder – can be expanded later
  };

  return (
    <div
      id="panel-creations"
      role="tabpanel"
      aria-labelledby="tab-creations"
      className="creations-analytics"
    >
      <div className="creations-analytics__toolbar">
        <input
          type="search"
          className="creations-analytics__search"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search creations"
        />
        {/* <button
          type="button"
          className="creations-analytics__filter-btn"
          onClick={handleFilterClick}
          aria-label="Filter creations"
        >
          <Icon alias="filter" width={20} height={20} color="#FF3232" />
        </button> */}
      </div>

      {loading ? (
        <AnalyticsSubtabListSkeleton variant="creations" />
      ) : filteredCreations.length === 0 ? (
        <div className="creations-analytics__empty">
          <p>No creations found</p>
        </div>
      ) : (
        <ul className="creations-analytics__list" role="list">
          {filteredCreations.map((creation) => (
            <li
              key={creation.creation_id}
              className="creations-analytics__item"
              role="listitem"
            >
              <div
                className="creations-analytics__card"
                onClick={() => handleCreationClick(creation)}
                onKeyDown={(e) => handleCreationKeyDown(e, creation)}
                role="button"
                tabIndex={0}
                aria-label={`View ${creation.title || 'creation'}`}
              >
                <div className="creations-analytics__thumb">
                  {creation.thumbnail_url ? (
                    <img src={creation.thumbnail_url} alt={creation.title || ''} />
                  ) : (
                    <div className="creations-analytics__placeholder" />
                  )}
                  <div className="creations-analytics__overlays">
                    <Icon
                      alias={GENRE_ICONS[creation.category?.toLowerCase()] || 'creations'}
                      width={20}
                      height={20}
                      color="#FFFFFF"
                    />
                    {Number(creation.price) > 0 && (
                      <Icon alias="shopping_bag" width={20} height={20} color="#FFFFFF" />
                    )}
                  </div>
                </div>
                <div className="creations-analytics__info">
                  <h3 className="creations-analytics__title">
                    {creation.title || 'Untitled'}
                  </h3>
                  <div className="creations-analytics__meta">
                    <span className="creations-analytics__views">
                      <Icon
                        alias="eye_views"
                        width={14}
                        height={14}
                        color="currentColor"
                      />
                      {typeof creation.views === 'number'
                        ? creation.views.toLocaleString()
                        : (creation.total_views ?? creation.view_count ?? 0).toLocaleString()}
                    </span>
                    <span className="creations-analytics__price">
                      <Icon alias="euro" width={14} height={14} color="currentColor" />
                      {formatEurosAmount(creation.price)}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CreationsAnalytics;
